// src/Components/Settings/TransactionHistoryModal.jsx
// Two tabs, one list at a time:
//   Purchases     — what was paid for with money (coin packs, Pro)
//   Coin activity — coins in (rewards, bonuses, refunds) and out (spending)
// billingHistory is the user's coinPurchaseHistory, which mixes paid purchases
// with free credits; spendingHistory is coinSpendingHistory.
import { useMemo, useState } from "react";
import { Crown, Gift, Minus, ReceiptText, Sparkles } from "lucide-react";
import BottomSheet from "../BottomSheet";
import NameItCoin from "../NameItCoin";
import { coinsOf, creditTitle, isPaidPurchase, purchaseTitle, spendTitle } from "../../Services/historyLabels";

const toDate = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWhen = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return "";
  return `${date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}, ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

// What the buyer actually paid: dollar purchases carry currency + paidAmount,
// older naira entries only have `price`.
const formatPaid = (entry) =>
  entry.currency === "USD" && entry.paidAmount != null
    ? `$${Number(entry.paidAmount).toFixed(2)}`
    : `₦${Number(entry.price ?? 0).toLocaleString()}`;

function Row({ icon, title, when, detail, amount, amountClass }) {
  return (
    <li className="flex items-center gap-3 py-3">
      <span className="w-9 h-9 rounded-xl bg-brand-surface2 flex items-center justify-center flex-shrink-0" aria-hidden="true">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brand-text truncate">{title}</p>
        <p className="text-xs text-brand-textSecondary truncate">
          {when}
          {detail && <span className="font-mono"> · {detail}</span>}
        </p>
      </div>
      <p className={`text-sm font-bold tabular-nums flex-shrink-0 ${amountClass}`}>{amount}</p>
    </li>
  );
}

function Empty({ title, body }) {
  return (
    <div className="text-center py-10">
      <ReceiptText className="w-9 h-9 text-brand-placeholder mx-auto mb-3" aria-hidden="true" />
      <p className="text-sm font-semibold text-brand-text">{title}</p>
      <p className="mt-1 text-xs text-brand-textSecondary">{body}</p>
    </div>
  );
}

const TransactionHistoryModal = ({ isOpen, onClose, billingHistory = [], spendingHistory = [] }) => {
  const [tab, setTab] = useState("purchases");

  const { purchases, activity } = useMemo(() => {
    const byNewest = (a, b) => (toDate(b.timestamp)?.getTime() || 0) - (toDate(a.timestamp)?.getTime() || 0);
    const paid = billingHistory.filter(isPaidPurchase);
    const credits = billingHistory
      .filter((entry) => !isPaidPurchase(entry) && coinsOf(entry) > 0)
      .map((entry) => ({ kind: "in", entry }));
    const spends = spendingHistory.map((entry) => ({ kind: "out", entry }));
    return {
      purchases: [...paid].sort(byNewest),
      activity: [...credits, ...spends].sort((a, b) => byNewest(a.entry, b.entry)),
    };
  }, [billingHistory, spendingHistory]);

  const tabs = [
    ["purchases", "Purchases", purchases.length],
    ["activity", "Coin activity", activity.length],
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Transaction history">
      <div role="tablist" aria-label="History" className="grid grid-cols-2 p-1 rounded-xl bg-brand-input mb-2">
        {tabs.map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`h-9 rounded-lg text-sm font-semibold transition-colors ${
              tab === key ? "bg-brand-surface text-brand-text shadow-[0_1px_2px_rgba(0,0,0,0.08)]" : "text-brand-textSecondary hover:text-brand-text"
            }`}
          >
            {label}
            {count > 0 && <span className="ml-1.5 text-xs font-medium text-brand-textSecondary tabular-nums">{count}</span>}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === "purchases" &&
          (purchases.length === 0 ? (
            <Empty title="No purchases yet" body="Coin packs and Pro you buy will show here." />
          ) : (
            <ul className="divide-y divide-brand-borderLight">
              {purchases.map((entry, i) => (
                <Row
                  key={`${entry.paymentId || "p"}-${i}`}
                  icon={String(entry.purpose || "").startsWith("pro") ? <Crown size={18} className="text-amber-600" /> : <NameItCoin size="small" />}
                  title={purchaseTitle(entry)}
                  when={formatWhen(entry.timestamp)}
                  detail={entry.paymentId ? `Ref ${entry.paymentId}` : null}
                  amount={formatPaid(entry)}
                  amountClass="text-brand-text"
                />
              ))}
            </ul>
          ))}

        {tab === "activity" &&
          (activity.length === 0 ? (
            <Empty title="No coin activity yet" body="Rewards you earn and coins you spend will show here." />
          ) : (
            <ul className="divide-y divide-brand-borderLight">
              {activity.map(({ kind, entry }, i) =>
                kind === "in" ? (
                  <Row
                    key={`in-${i}`}
                    icon={entry.purpose === "pro_monthly_bonus" ? <Sparkles size={18} className="text-amber-600" /> : <Gift size={18} className="text-green-600" />}
                    title={creditTitle(entry)}
                    when={formatWhen(entry.timestamp)}
                    amount={`+${coinsOf(entry).toLocaleString()}`}
                    amountClass="text-green-700"
                  />
                ) : (
                  <Row
                    key={`out-${i}`}
                    icon={<Minus size={18} className="text-brand-textSecondary" />}
                    title={spendTitle(entry.purpose)}
                    when={formatWhen(entry.timestamp)}
                    amount={`−${Number(entry.amount || 0).toLocaleString()}`}
                    amountClass="text-brand-text"
                  />
                )
              )}
            </ul>
          ))}
      </div>
    </BottomSheet>
  );
};

export default TransactionHistoryModal;
